import {
  BasicInsuranceModel,
  BasicInsuranceModelType,
  BasicInsurancePlan,
  BasicInsurancePlanType,
  GenderOrPrenatal,
} from '../../../api';
import { Item } from './tracking-ecommerce.events';
import {
  CATEGORY,
  CATEGORY_ID,
  CURRENCY_CHF,
  genderNames,
  mapAccidentCoverage,
  mapCustomerType,
  mapFranchise,
} from './tracking-ecommerce.map';
import { PersonBasicInsuranceState } from '../../../states/offer/offer.state';
import { priceIfPresentOrZero } from './tracking-ecommerce.utils';

const basicInsurancePlanIds: {
  [key in BasicInsurancePlanType]: string;
} = {
  GENERAL_PRACTITIONER_PLAN: 'general_practitioner_plan',
  TELEPHONE_PLAN: 'telephone_plan',
  COMBINATION_PLAN: 'combination_plan',
  TRADITIONAL_PLAN: 'traditional_plan',
  INTEGRATED_CARE_PLAN: 'integrated_care_plan',
};

const basicInsurancePlanNames: {
  [key in BasicInsurancePlanType]: string;
} = {
  GENERAL_PRACTITIONER_PLAN: 'Hausarztmodelle',
  TELEPHONE_PLAN: 'Telemedizinische Modelle',
  COMBINATION_PLAN: 'Kombiniertes Modell: Hausarzt und Telemedizin',
  TRADITIONAL_PLAN: 'Traditionelles Modell: Freie Arztwahl',
  //TODO: Fix Name: DAP-4171
  INTEGRATED_CARE_PLAN: 'Viva',
};

const basicInsuranceModelIds: {
  [key in BasicInsuranceModelType]: string;
} = {
  TRADITIONAL: 'traditional',
  MANAGED_CARE: 'managed_care',
  MED_DIRECT: 'med_direct',
  CASA_NOVA: 'casa_nova',
  TEL_DOC: 'tel_doc',
  MED_CALL: 'med_call',
  TEL_CARE: 'tel_care',
  COMBI_CARE: 'combi_care',
  //TODO Fix Name: DAP-4171
  VIVA: 'viva',
};

const basicInsuranceModelIdNames: {
  [key in BasicInsuranceModelType]: string;
} = {
  TRADITIONAL: 'Traditional',
  MANAGED_CARE: 'Managed Care',
  MED_DIRECT: 'Med Direct',
  CASA_NOVA: 'Casa Nova',
  TEL_DOC: 'Tel Doc',
  MED_CALL: 'Med Call',
  TEL_CARE: 'Tel Care',
  COMBI_CARE: 'Combi Care',
  //TODO Fix Name: DAP-4171
  VIVA: 'Viva',
};

const basicInsuranceModelNames: {
  [key in BasicInsuranceModelType]: string;
} = {
  TRADITIONAL: 'Traditionelles Modell: Freie Arztwahl',
  MANAGED_CARE: 'Hausarztmodelle',
  MED_DIRECT: 'Hausarztmodelle',
  CASA_NOVA: 'Hausarztmodelle',
  TEL_DOC: 'Telemedizinische Modelle',
  MED_CALL: 'Telemedizinische Modelle',
  TEL_CARE: 'Telemedizinische Modelle',
  COMBI_CARE: 'Kombiniertes Modell: Hausarzt und Telemedizin',
  //TODO Fix name:  DAP-4171
  VIVA: 'Integriert',
};

export function mapBasicInsuranceItem(basicInsurancePlan: BasicInsurancePlan, index: number): Item {
  return {
    item_id: basicInsurancePlanIds[basicInsurancePlan.type],
    item_name: basicInsurancePlanNames[basicInsurancePlan.type],
    currency: CURRENCY_CHF,
    index: index + 1,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.BASIC_INSURANCE,
    item_list_id: CATEGORY_ID.BASIC_INSURANCE,
    item_list_name: CATEGORY_ID.BASIC_INSURANCE,
    price: basicInsurancePlan.lowest_price,
    quantity: 1,
  };
}

export function mapBasicInsuranceItemByModel(basicInsuranceModel: BasicInsuranceModel, index: number): Item {
  return {
    item_id: basicInsuranceModelIds[basicInsuranceModel.type],
    item_name: basicInsuranceModelNames[basicInsuranceModel.type],
    currency: CURRENCY_CHF,
    index: index + 1,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.BASIC_INSURANCE,
    item_list_id: CATEGORY_ID.BASIC_INSURANCE,
    item_list_name: CATEGORY_ID.BASIC_INSURANCE,
    price: basicInsuranceModel.price,
    quantity: 1,
  };
}

export function mapBasicInsuranceVariantItem(basicInsuranceModel: BasicInsuranceModel, index: number): Item {
  return {
    item_id: basicInsuranceModelIds[basicInsuranceModel.type],
    item_name: basicInsuranceModelNames[basicInsuranceModel.type],
    currency: CURRENCY_CHF,
    index: index + 1,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.BASIC_INSURANCE,
    item_list_id: CATEGORY_ID.BASIC_INSURANCE,
    item_list_name: CATEGORY_ID.BASIC_INSURANCE,
    price: basicInsuranceModel.price,
    quantity: 1,
    item_variant: basicInsuranceModelIdNames[basicInsuranceModel.type],
  };
}

export function mapBasicInsuranceDetailItemFromModel(
  basicInsuranceModel: BasicInsuranceModel,
  franchise: string,
  includesAccidentCoverage: boolean,
  totalPersons: number,
  gender: GenderOrPrenatal,
): Item {
  return {
    item_id: basicInsuranceModelIds[basicInsuranceModel.type],
    item_name: basicInsuranceModelNames[basicInsuranceModel.type],
    currency: CURRENCY_CHF,
    index: 1,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.BASIC_INSURANCE,
    item_list_id: CATEGORY_ID.BASIC_INSURANCE,
    item_list_name: CATEGORY_ID.BASIC_INSURANCE,
    item_variant: basicInsuranceModelIdNames[basicInsuranceModel.type],
    price: basicInsuranceModel.price,
    quantity: 1,
    franchise: mapFranchise(franchise),
    accident_coverage: mapAccidentCoverage(includesAccidentCoverage),
    customer_type: mapCustomerType(totalPersons),
    gender: genderNames[gender],
  };
}

export function mapBasicInsuranceDetailItemFromState(
  basicInsurance: PersonBasicInsuranceState,
  index: number,
  totalPersons: number,
  gender: GenderOrPrenatal,
  purchaseType?: string,
): Item {
  return {
    item_id: basicInsuranceModelIds[basicInsurance.key.modelType],
    item_name: basicInsuranceModelNames[basicInsurance.key.modelType],
    currency: CURRENCY_CHF,
    index: index,
    item_category: CATEGORY.HEALTH_INSURANCE,
    item_category2: CATEGORY.BASIC_INSURANCE,
    item_list_id: CATEGORY_ID.BASIC_INSURANCE,
    item_list_name: CATEGORY_ID.BASIC_INSURANCE,
    item_variant: basicInsuranceModelIdNames[basicInsurance.key.modelType],
    price: priceIfPresentOrZero(basicInsurance.price),
    quantity: 1,
    franchise: mapFranchise(basicInsurance.franchise),
    accident_coverage: mapAccidentCoverage(basicInsurance.includesAccidentCoverage),
    customer_type: mapCustomerType(totalPersons),
    gender: genderNames[gender],
    insurance_provider: basicInsurance.key.insurer,
    purchase_type: purchaseType,
  };
}
